import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import 'react-toastify/dist/ReactToastify.css';

import PrivateRoute from './components/routes/PrivateRoute';
import PublicRoute from './components/routes/PublicRoute';
import PendenciasBanner from './components/pendencias-banner/PendenciasBanner';
import NavGlobal from './components/nav-global/NavGlobal';

import LoginPage from './views/auth/pages/login';
import AssociadoPage from './views/associado/pages/associado';
import CarroPage from './views/carro/pages/carro';
import DocumentoPage from './views/documento/pages/documento';
import ConcluidoPage from './views/concluido/pages/concluido';
import AssinaturaPage from './views/assinatura/pages/assinatura';
// As telas de assinatura e prévia da VISTORIA migraram para
// `views/vistoria/pages/` na at-64, com rotas `/vistoria/assinatura*` — antes
// viviam em `views/assinatura-termo/` sob `/assinatura-termo*`, nome que
// descrevia outro fluxo. Ver `views/vistoria/routes.ts`.
import TermoPage from './views/termo/pages/termo';
import AdesoesPage from './views/adesoes/pages/adesoes';
import ProdutosPage from './views/produto/pages/produto';
import CotacoesPage from './views/cotacao/pages/cotacao';
import CotacoesProdutoPage from './views/cotacao-produto/pages/cotacaoProduto';
import HomePage from './views/home/pages/home';
import TIPage from './views/ti/pages/ti';
import EnviosSgaPage from './views/ti/pages/enviosSga';
import UsuariosPage from './views/ti/pages/usuarios';
import UsuarioFichaPage from './views/ti/pages/usuarioFicha';
import CatalogoTermosPage from './views/ti/pages/catalogoTermos';
import TermoFichaPage from './views/ti/pages/termoFicha';
import CronsPage from './views/ti/pages/crons';
import LogsPage from './views/ti/pages/logs';
import PlanosCotacaoPage from './views/ti/pages/planosCotacao';
import PlanoFichaPage from './views/ti/pages/planoFicha';
import BikesEletricasPage from './views/ti/pages/bikesEletricas';
import BikeFichaPage from './views/ti/pages/bikeFicha';
import ContratosPage from './views/ti/pages/contratos';
import ExpurgoAssociadoPage from './views/ti/pages/expurgoAssociado';
import { LOGIN } from './views/auth/routes';
import { ASSOCIADO, ASSOCIADOEDIT, ASSOCIADOSUBSTITUIR } from './views/associado/routes';
import { CARRO, CARROEDIT, CARROSUBSTITUIR } from './views/carro/routes';
import { DOCUMENTO, DOCUMENTO_EDIT } from './views/documento/routes';
import { CONCLUIDO } from './views/concluido/routes';
import { ASSINATURA } from './views/assinatura/routes';
import { TERMO } from './views/termo/routes';
import { ADESAOSCONSULTAR, ADESOES } from './views/adesoes/routes';
import { PRODUTO } from './views/produto/routes';
import { COTACAO } from './views/cotacao/routes';
import { COTACAO_PRODUTO } from './views/cotacao-produto/routes';
import { HOME } from './views/home/routes';
import { CARTEIRA } from './views/carteira/routes';
import { ACOMPANHAMENTO_OFICINA } from './views/oficina/routes';
import {
  TI,
  TIENVIOS,
  TIUSUARIOS,
  TIUSUARIO,
  TIUSUARIO_NOVO,
  TITERMOS,
  TITERMO,
  TICRONS,
  TILOGS,
  TIPLANOS,
  TIPLANO,
  TIBIKES,
  TIBIKE,
  TIBIKE_NOVO,
  TICONTRATOS,
  TIEXPURGO,
} from './views/ti/routes';
import {
  VISTORIA,
  VISTORIA_ASSINATURA,
  VISTORIA_ASSINATURA_PREVIA,
  VISTORIA_CAPTURA,
  VISTORIA_CHECKLIST_ACESSORIOS,
  VISTORIA_LAUDO,
  VISTORIA_LAUDO_HTML_VEICULO,
} from './views/vistoria/routes';

import { RootState } from './store/store';
import { decodeJwtPayload, isTokenExpiradoComTolerancia } from './utils/jwt';
import FullScreenLoader from './components/loading/FullScreenLoader';
import SessaoIndisponivel from './components/loading/SessaoIndisponivel';
import { refreshOnce, avisarConexaoIndisponivel } from './api/http';
import { performLogout } from './utils/authLogout';
import AssociadoPageEdit from './views/associado/pages/associadoEdit';
import CarroEditPage from './views/carro/pages/carroEdit';
import DocumentoEditPage from './views/documento/pages/documentoEdit';
import AssociadoSubstituirPage from './views/associado/pages/associadoSubstituir';
import CarroSubstituirPage from './views/carro/pages/carroSubstituir';
import AdesaoConsultar from './views/adesoes/pages/adesaoConsultar';
import VistoriaBuscaPage from './views/vistoria/pages/vistoriaBusca';
import CarteiraPage from './views/carteira/pages/carteira';
import AcompanhamentoOficinaPage from './views/oficina/pages/AcompanhamentoOficina';
import VistoriaCapturaPage from './views/vistoria/pages/vistoriaCaptura';
import VistoriaChecklistPage from './views/vistoria/pages/vistoriaChecklist';
import VistoriaAssinaturaPage from './views/vistoria/pages/vistoriaAssinatura';
import VistoriaAssinaturaPreviaPage from './views/vistoria/pages/vistoriaAssinaturaPrevia';
import VistoriaLaudoPage from './views/vistoria/pages/vistoriaLaudo';
import VistoriaLaudoVeiculoPage from './views/vistoria/pages/vistoriaLaudoVeiculo';

/** Intervalo de nova tentativa quando o refresh falha por indisponibilidade. */
const RETENTATIVA_REFRESH_MS = 30 * 1000;

const App: React.FC = () => {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  // Se a renovação falhou por indisponibilidade NESTE carregamento da página.
  // É estado local de propósito: o flag equivalente no store (`sessaoIndisponivel`,
  // lido pelo `PrivateRoute` para não deslogar) é persistido pelo redux-persist e
  // poderia chegar `true` de uma sessão anterior, fazendo a tela de aviso piscar
  // antes da primeira tentativa. Aqui só liga depois de uma falha real de agora.
  const [renovacaoIndisponivel, setRenovacaoIndisponivel] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Evita repetir o toast a cada retentativa de fundo durante uma queda longa.
  // Falha de chamada iniciada pelo usuário continua avisando sempre (interceptor).
  const jaAvisouIndisponivel = useRef(false);

  /**
   * Renovação proativa/bootstrap. Usa o `refreshOnce` do cliente `http` (at-22):
   * antes daqui saía um axios PRÓPRIO, sem single-flight compartilhado — os dois
   * motores podiam renovar em paralelo e, com rotação de refresh token no
   * backend, quem perdia a corrida era deslogado em silêncio.
   *
   * Classificação da falha: só `expirada` (401/403) desloga; indisponibilidade
   * de rede mantém a sessão e reagenda nova tentativa.
   */
  const renovarToken = useCallback(async () => {
    setRenovacaoIndisponivel(false); // enquanto tenta, o gate mostra o loader
    const resultado = await refreshOnce();

    if (resultado.ok) {
      jaAvisouIndisponivel.current = false;
      return;
    }

    if (resultado.motivo === 'expirada') {
      await performLogout(resultado.motivoLogout);
      return;
    }

    // Indisponível: NÃO desloga. Avisa e tenta de novo mais tarde. O flag
    // compartilhado (`sessaoIndisponivel`) já foi marcado no store pelo
    // `refreshOnce`; este aqui só controla o que o gate renderiza.
    setRenovacaoIndisponivel(true);

    if (!jaAvisouIndisponivel.current) {
      jaAvisouIndisponivel.current = true;
      avisarConexaoIndisponivel();
    }

    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      void renovarToken();
    }, RETENTATIVA_REFRESH_MS);
  }, []);

  // Refresh proativo + bootstrap: agenda a renovação para 5 min antes da
  // expiração; se o token persistido já estiver expirado (ex.: recarregou a
  // página depois de horas), renova imediatamente. Reagenda ao voltar de
  // background (visibilitychange), pois timers longos são adiados em abas ocultas.
  useEffect(() => {
    if (!accessToken) return;

    const payload = decodeJwtPayload(accessToken);
    const exp = payload?.exp ? payload.exp * 1000 : null;

    if (!exp) return;

    const scheduleRefresh = () => {
      if (refreshTimer.current) {
        clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }

      const refreshTime = exp - Date.now() - 5 * 60 * 1000;

      if (refreshTime <= 0) {
        void renovarToken();
        return;
      }

      refreshTimer.current = setTimeout(() => {
        void renovarToken();
      }, refreshTime);
    };

    scheduleRefresh();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        scheduleRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (refreshTimer.current) {
        clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [accessToken, renovarToken]);

  useEffect(() => {
    const preventZoom = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };

    document.addEventListener('touchstart', preventZoom, { passive: false });

    return () => {
      document.removeEventListener('touchstart', preventZoom);
    };
  }, []);

  // Gate de bootstrap: se há um token persistido porém expirado (ex.: app reaberto
  // depois de horas), o refresh proativo (effect acima) já foi disparado. Enquanto
  // isso, mostrar o loader em vez de deixar as rotas decidirem — senão o PrivateRoute
  // manda pro /login e o PublicRoute (token ainda truthy) manda pra home, num
  // ping-pong. Quando o refresh resolve (token novo) ou o logout limpa o token,
  // este gate abre.
  //
  // at-22: como a falha de rede não desloga mais, o gate não pode ficar preso num
  // loader eterno quando o servidor está inalcançável — nesse caso mostra a tela
  // de aviso (sessão preservada + ação de tentar novamente).
  if (accessToken && isTokenExpiradoComTolerancia(accessToken)) {
    if (renovacaoIndisponivel) {
      return <SessaoIndisponivel onTentarNovamente={() => void renovarToken()} />;
    }
    return <FullScreenLoader />;
  }

  return (
    <BrowserRouter>
      {/* O ToastContainer da aplicação vive em src/index.tsx (at-19) — não montar outro aqui. */}
      <PendenciasBanner />
      {/*
        Navegação global (Plano D, at-32): fora do <Routes> e montada uma única
        vez, para valer em qualquer tela autenticada sem tocar nos headers de
        cada uma. O próprio componente se esconde quando não há sessão ou
        quando a rota é /login.
      */}
      <NavGlobal />
      <Routes>
        <Route path={LOGIN()} element={<PublicRoute element={<LoginPage />} />} />
        <Route path="/" element={<PrivateRoute element={<HomePage />} />} />
        <Route path="*" element={<PrivateRoute element={<HomePage />} />} />
        <Route path={TI()} element={<PrivateRoute element={<TIPage />} />} />
        <Route path={TIENVIOS()} element={<PrivateRoute element={<EnviosSgaPage />} />} />
        <Route path={TIUSUARIOS()} element={<PrivateRoute element={<UsuariosPage />} />} />
        {/* ⚠️ `/novo` ANTES de `/:id`: na ordem inversa, "novo" seria lido como um id
            e a ficha tentaria carregar um usuário chamado "novo" (at-68). */}
        <Route path={TIUSUARIO_NOVO()} element={<PrivateRoute element={<UsuarioFichaPage />} />} />
        <Route path={TIUSUARIO()} element={<PrivateRoute element={<UsuarioFichaPage />} />} />
        <Route path={TITERMOS()} element={<PrivateRoute element={<CatalogoTermosPage />} />} />
        {/* at-69: os três editores viraram fichas em sub-rota — eram modais de 692,
            509 e 463 linhas, com abas e duas colunas. */}
        <Route path={TITERMO()} element={<PrivateRoute element={<TermoFichaPage />} />} />
        <Route path={TICRONS()} element={<PrivateRoute element={<CronsPage />} />} />
        <Route path={TILOGS()} element={<PrivateRoute element={<LogsPage />} />} />
        <Route path={TIPLANOS()} element={<PrivateRoute element={<PlanosCotacaoPage />} />} />
        <Route path={TIPLANO()} element={<PrivateRoute element={<PlanoFichaPage />} />} />
        <Route path={TIBIKES()} element={<PrivateRoute element={<BikesEletricasPage />} />} />
        {/* ⚠️ `/novo` ANTES de `/:id`, como em `/ti/usuarios`: na ordem inversa
            "novo" seria lido como um id e a ficha tentaria carregar uma
            motocicleta elétrica chamada "novo". */}
        <Route path={TIBIKE_NOVO()} element={<PrivateRoute element={<BikeFichaPage />} />} />
        <Route path={TIBIKE()} element={<PrivateRoute element={<BikeFichaPage />} />} />
        <Route path={TICONTRATOS()} element={<PrivateRoute element={<ContratosPage />} />} />
        {/*
          at-77: exclusão definitiva, SÓ ADMIN. O `PrivateRoute` é de autenticação,
          não de papel — quem barra é o backend, com 403, e a tela mostra o motivo.
          O card no hub `/ti` fica escondido para quem não é ADMIN.
        */}
        <Route path={TIEXPURGO()} element={<PrivateRoute element={<ExpurgoAssociadoPage />} />} />

        <Route path={ASSOCIADO()} element={<PrivateRoute element={<AssociadoPage />} />} />
        <Route path={ASSOCIADOEDIT()} element={<PrivateRoute element={<AssociadoPageEdit />} />} />
        <Route path={ASSOCIADOSUBSTITUIR()} element={<PrivateRoute element={<AssociadoSubstituirPage />} />} />
        <Route path={CARRO()} element={<PrivateRoute element={<CarroPage />} />} />
        <Route path={CARROEDIT()} element={<PrivateRoute element={<CarroEditPage />} />} />
        <Route path={CARROSUBSTITUIR()} element={<PrivateRoute element={<CarroSubstituirPage />} />} />
        <Route path={DOCUMENTO()} element={<PrivateRoute element={<DocumentoPage />} />} />
        <Route path={DOCUMENTO_EDIT()} element={<PrivateRoute element={<DocumentoEditPage />} />} />
        <Route path={CONCLUIDO()} element={<PrivateRoute element={<ConcluidoPage />} />} />
        <Route path={ASSINATURA()} element={<PrivateRoute element={<AssinaturaPage />} />} />
        <Route path={TERMO()} element={<PrivateRoute element={<TermoPage />} />} />
        <Route path={ADESOES()} element={<PrivateRoute element={<AdesoesPage />} />} />
        <Route path={ADESAOSCONSULTAR()} element={<PrivateRoute element={<AdesaoConsultar />} />} />
        <Route path={PRODUTO()} element={<PrivateRoute element={<ProdutosPage />} />} />
        <Route path={COTACAO()} element={<PrivateRoute element={<CotacoesPage />} />} />
        <Route path={COTACAO_PRODUTO()} element={<PrivateRoute element={<CotacoesProdutoPage />} />} />
        <Route path={HOME()} element={<PrivateRoute element={<HomePage />} />} />
        <Route path={CARTEIRA()} element={<PrivateRoute element={<CarteiraPage />} />} />
        <Route
          path={ACOMPANHAMENTO_OFICINA()}
          element={<PrivateRoute element={<AcompanhamentoOficinaPage />} />}
        />
        <Route path={VISTORIA()} element={<PrivateRoute element={<VistoriaBuscaPage />} />} />
        <Route path={VISTORIA_CAPTURA()} element={<PrivateRoute element={<VistoriaCapturaPage />} />} />
        <Route
          path={VISTORIA_CHECKLIST_ACESSORIOS()}
          element={<PrivateRoute element={<VistoriaChecklistPage />} />}
        />
        {/* ⚠️ A prévia é declarada ANTES da assinatura só por clareza de leitura:
            aqui não há conflito de casamento (são caminhos distintos, não um
            param), diferente do `/contratos/p/:token` × `/contratos/:id`. */}
        <Route
          path={VISTORIA_ASSINATURA_PREVIA()}
          element={<PrivateRoute element={<VistoriaAssinaturaPreviaPage />} />}
        />
        <Route
          path={VISTORIA_ASSINATURA()}
          element={<PrivateRoute element={<VistoriaAssinaturaPage />} />}
        />
        <Route path={VISTORIA_LAUDO()} element={<PrivateRoute element={<VistoriaLaudoPage />} />} />
        <Route
          path={VISTORIA_LAUDO_HTML_VEICULO()}
          element={<PrivateRoute element={<VistoriaLaudoVeiculoPage />} />}
        />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
